/**
 * Breadcrumbs services for module mwApp.common
 */

/**
 * breadcrumbsSvc service
 *
 * @class breadcrumbsSvc
 * @memberof mwApp/common
 * @see mwApp/common
 *
 * @ngdoc service
 * @name mwApp/common.service:breadcrumbsSvc
 */
/* @ngInject */
export default function BreadcrumbsSvc ($rootScope, $transitions, $location, $state, $interpolate, authSvc) {
  /**
   * @typedef {Object} Breadcrumb
   * @property {String} name - breadcrumb name
   * @property {String} path - breadcrumb path
   * @property {String} label - breadcrumb label
   * @memberof mwApp/common.service:breadcrumbsSvc
   * @description Object, describing single breadcrumb
   */

  /**
   * All states
   *
   * @private
   * @memberof mwApp/common.service:breadcrumbsSvc~
   * @type {Object[]}
   */
  var states = $state.get()

  /**
   * breadcrumbs array
   *
   * @private
   * @memberof mwApp/common.service:breadcrumbsSvc~
   * @type {mwApp/common.service:breadcrumbsSvc.Breadcrumb[]}
   */
  var breadcrumbs = []

  /**
   * Get state object from states by url
   *
   * @memberof mwApp/common.service:breadcrumbsSvc#
   * @param  {String} url - url
   * @return {Object} state and parameters
   */
  var getStateByUrl = function (url) {
    for (var i = 0; i < states.length; i++) {
      if (!states[i].abstract && states[i].$$state) {
        var privatePortion = states[i].$$state()
        var match = privatePortion.url ? privatePortion.url.exec(url) : null
        if (match) {
          return {
            state: states[i],
            params: match
          }
        }
      }
    }
  }

  /**
   * Join path up to index
   *
   * @private
   * @memberof mwApp/common.service:breadcrumbsSvc~
   * @param  {String[]} pathElements - path, splitted by '/'
   * @param  {Number} index - element index
   * @return {String}
   */
  var getPath = function (pathElements, index) {
    return '/' + pathElements.slice(0, index + 1).join('/')
  }

  /**
   * Return state label
   *
   * @memberof mwApp/common.service:breadcrumbsSvc#
   * @param  {Object} state    - state object and params
   * @param  {String} [params] - state parameters
   * @param  {String} [def]    - default label
   * @param  {Object} [scope]  - scope for label interpolation
   * @return {String}
   */
  var getStateLabel = function (state, params, def, scope) {
    // skip state if it is restricted by access permissions
    // or if it's label is null
    if ((state.access && !authSvc.hasAccess(state.access, params)) || state.label === null) {
      return null
    }

    switch (typeof state.label) {
      case 'function':
        return state.label(angular.extend({}, params, scope))
      case 'string':
        return state.label.indexOf('{') !== -1
          ? $interpolate(state.label)(angular.extend({}, params, scope))
          : state.label
      case 'undefined':
        return def
      default:
        return String(state.label)
    }
  }

  /**
   * Update breadcrumbs
   *
   * @memberof mwApp/common.service:breadcrumbsSvc#
   * @param  {Object} [scope]
   */
  var updateBreadcrumbs = function (scope) {
    // get breadcrumbs

    var result = []
    var url = $location.path().replace(/\/+$/, '')
    var pathElements = url.split('/')
    var path, state, i, label

    pathElements.length > 1 && pathElements.shift()
    for (i = 0; i < pathElements.length; i++) {
      path = getPath(pathElements, i)
      state = getStateByUrl(path)
      if (state) { // add to header menu only existing state
        label = getStateLabel(state.state, state.params, pathElements[i], scope)
        if (label !== null) { // skip state if label === null
          result.push({
            name: pathElements[i],
            path: path.slice(1),
            // state: state,
            label: label
          })
        }
      }
    }

    breadcrumbs = result
  }

  /**
   * Get breadcrumbs array
   *
   * @name mwApp/common.service:breadcrumbsSvc#get
   * @function
   * @return {mwApp/common.service:breadcrumbsSvc.Breadcrumb[]}
   */
  this.get = function () {
    return breadcrumbs
  }

  /**
   * Get breadcrumbs array tail (last 3 elements)
   *
   * @name mwApp/common.service:breadcrumbsSvc#get
   * @function
   * @return {mwApp/common.service:breadcrumbsSvc.Breadcrumb[]}
   */
  this.tail = function () {
    if (breadcrumbs.length > 3) {
      return breadcrumbs.slice(-3)
    }
    return breadcrumbs
  }

  /**
   * Get breadcrumbs array head
   *
   * @name mwApp/common.service:breadcrumbsSvc#get
   * @function
   * @return {mwApp/common.service:breadcrumbsSvc.Breadcrumb[]}
   */
  this.head = function () {
    if (breadcrumbs.length > 3) {
      return breadcrumbs.slice(0, breadcrumbs.length - 3)
    }
  }

  /**
   * Check, if breadcrumbs array has head
   *
   * @name mwApp/common.service:breadcrumbsSvc#get
   * @function
   * @return {Boolean}
   */
  this.hasHead = function () {
    return breadcrumbs.length > 3
  }

  /**
   * Get first breadcrumb in array
   *
   * @name mwApp/common.service:breadcrumbsSvc#first
   * @function
   * @return {mwApp/common.service:breadcrumbsSvc.Breadcrumb}
   */
  this.first = function () {
    return breadcrumbs[0] || {}
  }

  /**
   * Get last breadcrumb in array
   *
   * @name mwApp/common.service:breadcrumbsSvc#last
   * @function
   * @return {mwApp/common.service:breadcrumbsSvc.Breadcrumb}
   */
  this.last = function () {
    return breadcrumbs[breadcrumbs.length - 1] || {}
  }

  /**
   * Set new scope for current breadcrumbs
   *
   * @deprecated
   * @name mwApp/common.service:breadcrumbsSvc#scope
   * @function
   * @return {mwApp/common.service:breadcrumbsSvc.Breadcrumb}
   */
  this.scope = function (scope) {
    updateBreadcrumbs(scope)
  }

  // Add UI Router `.onSuccess` Transition Hook
  // https://ui-router.github.io/guide/ng1/migrate-to-1_0#state-change-events
  $transitions.onSuccess({},
    (trans) => {
      if (!trans.dynamic()) {
        let uiinjector = trans.injector()
        let scope = {}
        uiinjector.context.getTokens().forEach(
          token => {
            if (typeof token === 'string' && token[0] !== '$') {
              scope[token] = uiinjector.get(token)
            }
          }
        )
        updateBreadcrumbs(scope)
      }
    }
  )
}
